import { useRef, useEffect } from 'react'

// Import focused hooks
import useRealtimeChoices from './useRealtimeChoices'
import useRealtimeVotes from './useRealtimeVotes'
import useRealtimePresence from './useRealtimePresence'
import useRealtimeChannel from './useRealtimeChannel'

/**
 * Custom hook for managing realtime subscriptions for poll updates
 * This is a composition hook that combines focused hooks for:
 * - Choice events (INSERT, UPDATE, DELETE)
 * - Vote events (INSERT, UPDATE, DELETE) with optimistic update tracking
 * - Presence tracking (active viewers)
 * - Channel lifecycle (setup, teardown, reconnection)
 *
 * @param {string} pollId - The poll ID (short code)
 * @param {Object} poll - The poll object (contains UUID as poll.id)
 * @param {Array} choices - Current choices array
 * @param {Function} setChoices - Function to update choices
 * @param {Function} setPoll - Function to update poll
 * @param {string} deviceId - Current device ID
 * @returns {Object} Hook state and functions
 */
function useRealtimeSubscription(pollId, poll, choices, setChoices, setPoll, deviceId) {
  // Ref to track current choices for event filtering
  const choicesRef = useRef([])

  // Keep choicesRef in sync
  useEffect(() => {
    choicesRef.current = choices
  }, [choices])

  // Compose focused hooks
  const choiceHandlers = useRealtimeChoices(setChoices, setPoll, pollId)

  const {
    pendingVotesRef,
    pendingRemovalsRef,
    processVoteInsert,
    processVoteUpdate,
    processVoteDelete
  } = useRealtimeVotes(setChoices, pollId, deviceId, choicesRef)

  const {
    activeViewers,
    handlePresenceSync,
    resetActiveViewers
  } = useRealtimePresence(pollId)

  // Pass handlers directly - useRealtimeChannel stores them in refs internally
  const {
    realtimeStatus,
    setupRealtimeSubscription,
    cleanupRealtimeSubscription,
    reconnect
  } = useRealtimeChannel({
    pollId,
    pollUuid: poll?.id,
    deviceId,
    choiceHandlers,
    voteHandlers: {
      processVoteInsert,
      processVoteUpdate,
      processVoteDelete
    },
    presenceHandlers: {
      handlePresenceSync,
      resetActiveViewers
    }
  })

  // Set up subscription when poll is loaded
  // Only depends on pollId, poll?.id - setupRealtimeSubscription is now stable
  useEffect(() => {
    if (poll && pollId) {
      setupRealtimeSubscription()
    }

    return () => {
      cleanupRealtimeSubscription()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollId, poll?.id, setupRealtimeSubscription, cleanupRealtimeSubscription])

  // Return unchanged public API
  return {
    realtimeStatus,
    activeViewers,
    reconnect,
    pendingVotesRef,
    pendingRemovalsRef
  }
}

export default useRealtimeSubscription
